import { isAfter } from '@fmnts/core/chronos';
import moment, { MomentInput } from 'moment';
import { ConditionBase, ConditionBuilder } from '../condition.type';

export interface ConditionAfter extends ConditionBase {
  /**
   * The value of the related field must come chronologically after this
   * value for the condition to be fulfilled
   */
  readonly after: moment.MomentInput;
  /**
   * Set to `true`, if the condition should be fulfilled for the same date
   *
   * @default false
   */
  readonly exclusive?: boolean;
  /**
   * Can be used to control the checks granularity.
   *
   * @default 'day'
   */
  readonly granularity?: moment.unitOfTime.StartOf;
}

/**
 * Type guard that checks if an object fulfills the `ConditionAfter` interface
 *
 * @param c Non nullish object
 * @returns `true` if `c` satisfies `ConditionAfter`
 */
export function isConditionAfter(c: Record<string, any>): c is ConditionAfter {
  return 'after' in c && moment(c['after'] as MomentInput, true).isValid();
}

/**
 * @param condition Condition options
 *
 * @returns
 * Condition function that returns `true`, if the passed `value` comes
 * chronologically **after** specified by the condition.
 *
 * @example
 * after({after: '1999-12-31'})('2000-01-01') // true
 * after({after: '2000-01-02'})('2000-01-01') // false
 *
 * @see {@link https://momentjs.com/docs/#/query/is-after/ }
 */
export const after: ConditionBuilder<ConditionAfter, moment.MomentInput> = (
  condition,
) => {
  const { after: other, exclusive, granularity = 'day' } = condition;

  return isAfter(other, { exclusive, granularity });
};
